/**
 * DashboardView — presentational practitioner home (no data hooks).
 *
 * A clean port of Flutter `dashboard_module` (`dashboard_screen.dart`) —
 * the intent, not the dummy cards. Flutter's dashboard is a 3-pane shell:
 * a left "progress / inbox" rail (STUB data — omitted per the parity gate),
 * a centre content area with a "Phone Call" action, and a right pane that
 * hosts the phone-call note widget (`create_telephone_appointment_widget`).
 *
 * This home keeps the two REAL surfaces:
 *   1. A greeting header + the "Phone Call" action (Flutter's content-header
 *      button that opens the phone-call note capture).
 *   2. Today's worklist — the actual appointment data — reused verbatim from
 *      `scheduling/todays-appointments-view.tsx` (injected as `worklist`).
 * The phone-call note capture (the one backed feature) slides in as a right
 * drawer over the worklist when opened (main's drawer and focus trap; the page
 * header around it has the new look).
 *
 * The Flutter stub cards (progress/inbox — `progress_card_widget`,
 * `inbox_card_widget`) have no backend and are intentionally dropped
 * (CLAUDE.md anti-fiction / parity gate).
 *
 * Composes DS: PageScaffold (`dashboard` variant) · PageScaffoldDrawer · the app PageHeader ·
 * Panel · Button · Icon. The page frame and the drawer (scrim + right-pinned
 * `role="dialog"` container) are the DS's; the drawer body is the DS `Panel`
 * (sticky title bar, close control, Escape-to-dismiss). This file keeps only the
 * focus management and the `inert` marking. Zero raw hex, no lucide.
 *
 * a11y (Andrew review, PR #225; Codex follow-up review): `Panel` renders
 * `role="region"` with no focus-trap, which is an a11y gap for a modal side
 * drawer — keyboard focus can escape behind the scrim. Checked the DS
 * package for a `Drawer` (or a drawer-placed, focus-trapping `Modal`) first:
 * neither exists — the DS `Modal` (atoms/Modal) is hard-coded to a centred
 * layout (no placement/drawer variant), so swapping it in would change the
 * drawer's visual treatment, not just add semantics.
 *
 * First attempt (later found incomplete by Codex): `role="dialog"` +
 * `aria-modal="true"` + a small dependency-free DOM-subtree focus trap
 * (`useFocusTrap`, cycling Tab/Shift+Tab within `drawerRef`). That trap only
 * contains focus within the drawer's own DOM subtree. The phone-call panel
 * renders a `PatientPicker` whose `PopoverContent` (base-ui `Popover`, see
 * `components/ui/popover.tsx`) portals its listbox to `document.body` (the
 * default `FloatingPortal` container — see `PopoverRoot`/`FloatingPortal` in
 * `@base-ui/react`) — i.e. OUTSIDE `drawerRef`'s DOM subtree. Tab cycling
 * scoped to `drawerRef` never sees that content, so focus could reach the
 * still-mounted "Phone Call" header button once it left the drawer via the
 * popover.
 *
 * Considered `@base-ui/react`'s `FloatingFocusManager` (it already ships as
 * a dependency and is portal-aware) before reaching for `inert`: it's what
 * base-ui's own `Dialog`/`Popover` primitives use internally, but (a) it's
 * not part of the package's public `exports` map — only re-exported from
 * `@base-ui/react/floating-ui-react`, an internal subpath `tsc` cannot even
 * resolve (`Cannot find module '@base-ui/react/floating-ui-react'`) — and
 * (b) more fundamentally, base-ui's `Popover.Root` only shares a
 * `FloatingTree` with an ANCESTOR `Popover`/`Menu` (submenu-style nesting);
 * it always mounts its own independent tree otherwise (see
 * `PopoverRoot.js`: `usePopoverRootContext(true) ? ... : <FloatingTree>`).
 * A `Dialog` wrapping the drawer would therefore never see the
 * `PatientPicker` popover as "inside" its trap — same failure mode as the
 * hand-rolled trap, just with an unsupported import to boot. So the
 * fix here is native `inert` (Option B): everything OUTSIDE the drawer —
 * the `PageHeader` (incl. the "Phone Call" trigger) and the worklist body —
 * is marked `inert` while the drawer is open, which removes them from the
 * tab order/click-through regardless of DOM nesting. The `PatientPicker`
 * popover portals to `document.body`, a sibling of the app root, so it is
 * never inside anything this component marks `inert` — it stays reachable.
 * `useFocusTrap` is kept for the parts `inert` doesn't cover: moving focus
 * into the drawer on open, restoring focus to the trigger on close, and
 * Tab/Shift+Tab cycling among the drawer's own (non-portaled) controls.
 * Escape-to-close is already provided by `Panel` itself.
 *
 * Shell-level containment (second Codex follow-up): the `inert` marking above
 * only reaches DOM this component renders. The persistent `PractitionerNav`
 * app rail is mounted one level up, in `AppShell` (`src/app/app-shell.tsx`) —
 * a sibling of the routed `<Outlet/>`, not a descendant of this component —
 * so it stayed clickable while the drawer was open (a user could navigate
 * away via the rail and lose the phone-call draft). Closed via a tiny shared
 * store, `usePhoneCallDrawerStore` (`src/dashboard/store/phone-call-drawer-store.ts`,
 * mirrors the existing `usePrintPreviewStore` pattern): this component's
 * `phoneCallOpen` prop is now backed by that store (set in `dashboard-page.tsx`),
 * and `AppShell` reads the same store to mark ONLY the nav rail `inert` —
 * full modal containment, not just this page's local subtree.
 */
import type { ReactNode } from 'react';
import { Button, PageScaffold } from '@scripsteam/scrips-signal-ds';
import { PhoneCallDialog } from './phone-call-dialog';
import { PageHeader } from '@/shared/ui/components/page-header';
import { V2_PAGE_TITLE } from '@/shared/ui/design-v2';
import { Phone } from 'lucide-react';

export interface DashboardViewProps {
  /** "Good morning, Aisha Khan" — time-of-day greeting + practitioner name. */
  greeting: string;
  /** Sub-line under the greeting (the long date, or the ready list's count line). */
  dateLabel: ReactNode;
  /** The ready-list home draws no Phone Call button in its header (Samer, 21 Oct; the design's TodayScreen header has none).
   *  Off: the primary button, as before. */
  hidePhoneCall?: boolean;
  /** Today's worklist surface, injected by the container (the reused Today view). */
  worklist: ReactNode;
  /** Whether the phone-call drawer is open. */
  phoneCallOpen: boolean;
  onOpenPhoneCall: () => void;
  onClosePhoneCall: () => void;
  /** The phone-call capture panel, rendered inside the drawer when open. */
  phoneCallPanel: ReactNode;
}

export function DashboardView({
  greeting,
  dateLabel,
  hidePhoneCall = false,
  worklist,
  phoneCallOpen,
  onOpenPhoneCall,
  onClosePhoneCall,
  phoneCallPanel,
}: DashboardViewProps) {
  // PROD-2743 design sheet §2.2: the greeting is the page title on the DS type scale
  // (`--text-lg`, semibold, text-primary).
  return (
    <PageScaffold
      variant="dashboard"
      // `inert` while the drawer is open, on both the header region (incl. the "Phone Call"
      // trigger Codex flagged as still reachable) and the worklist body: non-focusable,
      // non-clickable and hidden from AT regardless of DOM nesting — see the file header for
      // why this replaces relying solely on the DOM-subtree-scoped `useFocusTrap`. The drawer
      // sits in the scaffold's overlay slot, a sibling of the body, so it is never caught by it.
      // Showcase (design log 13 Oct): the one page header (PageHeader: 80px, 16 all round, title
      // 24/32 over the date, Phone Call on the subline's line) and a white page (D-60, no grey
      // container inside the white frame). The embedded Today worklist brings its own page
      // padding (48 sides, 32 top, 48 bottom).
      className="bg-transparent"
      headerProps={{ inert: phoneCallOpen }}
      bodyProps={{ inert: phoneCallOpen }}
      header={
        <PageHeader
          title={
            <span className={V2_PAGE_TITLE} data-testid="dashboard-title-v2">
              {greeting}
            </span>
          }
          meta={dateLabel}
          actions={
            hidePhoneCall ? undefined : (
              <Button variant="primary" size="md" icon={<Phone className="size-4" />} iconPosition="leading" onClick={onOpenPhoneCall}>
                Phone Call
              </Button>
            )
          }
        />
      }
      overlay={
        // Right drawer (main): the DS scrim (click to dismiss) + a modal dialog container, holding the
        // DS Panel. Background containment (incl. the portaled PatientPicker popover case)
        // comes from `inert` above, not from scoping a trap to this subtree, so picking a patient in
        // the portaled popover never dismisses the drawer.
        // The drawer, its focus trap and the design's centred-modal look live in PhoneCallDialog (shared with Today).
        <PhoneCallDialog open={phoneCallOpen} onClose={onClosePhoneCall}>
          {phoneCallPanel}
        </PhoneCallDialog>
      }
    >
      {worklist}
    </PageScaffold>
  );
}
